//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { mapGetters } from 'vuex';
import { formatDayLabel, formatDayLabelShort } from './scheduleResultGrouping.js';

export default {
  name: 'DayTabs',

  data() {
    return {
      printMenuOpen: false,
      isTeleported: false,
      stripFitsBesideActions: true,
      hasMeasured: false,
      forceShortLabels: false,
      measuring: false,
    };
  },

  computed: {
    ...mapGetters(['allDays', 'selectedDayValue']),

    dayFilters() {
      return this.allDays.map(d => ({
        ...d,
        selected: d.value === this.selectedDayValue,
      }));
    },

    dayLabelMode() {
      // Short labels when count rule says so (≥5) OR when measurement forced short
      // because the long labels didn't fit beside the actions.
      return (this.forceShortLabels || this.dayFilters.length >= 5) ? 'short' : 'full';
    },

    dayLabelFormatter() {
      return this.dayLabelMode === 'full' ? formatDayLabel : formatDayLabelShort;
    },
  },

  watch: {
    dayFilters() {
      this.queueMeasure();
    },
  },

  mounted() {
    document.addEventListener('mousedown', this.onOutsideClick);
    document.addEventListener('keydown', this.onPrintKeydown);
    // Drive measurement off of window resize (an external signal) instead of a
    // strip ResizeObserver. The strip's size also changes due to our own DOM
    // mutations during a measurement (label switches, --stacked toggle), and
    // listening for those creates a feedback loop on top of the multi-await
    // measurement cycle.
    this.onWindowResizeForMeasure = this.onWindowResizeForMeasure.bind(this);
    window.addEventListener('resize', this.onWindowResizeForMeasure);
    this.$nextTick(() => {
      this.setupTeleport();
      this.queueMeasure();
    });
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutsideClick);
    document.removeEventListener('keydown', this.onPrintKeydown);
    window.removeEventListener('resize', this.onWindowResizeForMeasure);
    if (this.measureRAF) {
      cancelAnimationFrame(this.measureRAF);
      this.measureRAF = null;
    }
    this.teardownTeleport();
  },

  methods: {
    formatDayLabel,

    selectDay(day) {
      if (day.value === this.selectedDayValue) return;
      this.$store.commit('setSelectedDay', day.value);
      this.$store.commit('setPageIndex', 0);
      this.$store.dispatch('executeSearch');
    },

    selectAllDays() {
      if (!this.selectedDayValue) return;
      this.$store.commit('setSelectedDay', null);
      this.$store.commit('setPageIndex', 0);
      this.$store.dispatch('executeSearch');
    },

    togglePrintMenu() {
      this.printMenuOpen = !this.printMenuOpen;
    },

    onPrint(scope) {
      this.printMenuOpen = false;
      this.$emit('print', scope);
    },

    onOutsideClick(event) {
      if (!this.printMenuOpen) return;
      const root = this.$refs.printRoot;
      if (root && !root.contains(event.target)) {
        this.printMenuOpen = false;
      }
    },

    onPrintKeydown(event) {
      if (event.key === 'Escape' && this.printMenuOpen) {
        this.printMenuOpen = false;
      }
    },

    async measureDayTabs() {
      if (this.measuring) return;
      const strip = this.$refs.dayTabsStrip;
      const tabs = (this.$refs.dayTabEls || []).filter(Boolean);
      if (!strip || tabs.length === 0) return;

      this.measuring = true;
      try {
        await this.runMeasurementPass(strip);
      } finally {
        this.measuring = false;
      }
    },

    onWindowResizeForMeasure() {
      // Coalesce rapid resize events into one measurement per animation frame.
      if (this.measureRAF) return;
      this.measureRAF = requestAnimationFrame(() => {
        this.measureRAF = null;
        this.measureDayTabs();
      });
    },

    async runMeasurementPass(strip) {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

      const tabs = (this.$refs.dayTabEls || []).filter(Boolean);
      if (tabs.length === 0) return;

      // On mobile, the SCSS .day-tab { min-width: 100px } floor governs sizing
      // and the strip uses its own scroll-with-fade pattern. Clear any inline
      // min-width and any forceShortLabels state from a previous desktop pass.
      if (!isDesktop) {
        tabs.forEach(t => { t.style.minWidth = ''; });
        this.forceShortLabels = false;
        this.hasMeasured = true;
        return;
      }

      const previousVisibility = strip.style.visibility;
      strip.style.visibility = 'hidden';

      // Always begin a measurement cycle with count-based default labels so we
      // can re-evaluate label format from scratch each time.
      if (this.forceShortLabels) {
        this.forceShortLabels = false;
        await this.$nextTick();
      }

      const measureAndCheckFit = () => {
        const liveTabs = (this.$refs.dayTabEls || []).filter(Boolean);
        liveTabs.forEach(t => { t.style.minWidth = ''; });

        let max = 0;
        liveTabs.forEach(t => {
          const w = t.getBoundingClientRect().width;
          if (w > max) max = w;
        });

        const tabMinWidth = Math.ceil(max);
        liveTabs.forEach(t => { t.style.minWidth = `${tabMinWidth}px`; });

        const dayTabsRoot = this.$refs.dayTabs;
        const allDaysEl = strip.querySelector('.day-tab--all-days');
        const actionsEl = dayTabsRoot ? dayTabsRoot.querySelector('.day-tabs__actions') : null;
        if (!dayTabsRoot || !actionsEl) return true;

        // Actions' natural width = sum of children widths + inter-child gaps.
        // This is independent of whether actions is currently stretched (in
        // the stacked layout) or naturally sized (side-by-side), so the fit
        // decision is stable across re-measures from any starting layout.
        const actionsChildren = Array.from(actionsEl.children);
        let actionsWidth = 0;
        actionsChildren.forEach(c => {
          actionsWidth += c.getBoundingClientRect().width;
        });
        const actionsGap = 12; // matches .day-tabs__actions { gap: 12px }
        if (actionsChildren.length > 1) {
          actionsWidth += (actionsChildren.length - 1) * actionsGap;
        }

        const allDaysWidth = allDaysEl ? allDaysEl.getBoundingClientRect().width : 0;
        const stripGap = 16; // matches .day-tabs__strip { gap: 16px }
        const rootGap = 12;  // matches .day-tabs desktop { gap: 12px }
        const stripNeededWidth = allDaysWidth + liveTabs.length * tabMinWidth + liveTabs.length * stripGap;
        const availableWidth = dayTabsRoot.clientWidth - actionsWidth - rootGap;
        return stripNeededWidth <= availableWidth;
      };

      let fits = measureAndCheckFit();

      // If default labels don't fit AND we have ≤4 days (currently long),
      // try shortening the labels.
      if (!fits && this.dayFilters.length <= 4) {
        this.forceShortLabels = true;
        await this.$nextTick();
        fits = measureAndCheckFit();

        if (!fits) {
          // Short labels also don't fit — we're going to stack. Stacked mode
          // uses count-based default labels (≤4 long, ≥5 short), so revert
          // forceShortLabels and re-measure tab widths for the long labels.
          this.forceShortLabels = false;
          await this.$nextTick();
          measureAndCheckFit();
        }
      }

      this.stripFitsBesideActions = fits;
      this.hasMeasured = true;

      // Wait for Vue to apply the --stacked class binding (and any final
      // label-mode change) before restoring visibility and re-evaluating
      // sticky/teleport state against the new layout.
      await this.$nextTick();
      strip.style.visibility = previousVisibility || '';
      this.updateTeleportState();
    },

    queueMeasure() {
      this.$nextTick(() => this.measureDayTabs());
    },

    setupTeleport() {
      const dayTabsEl = this.$refs.dayTabs;
      if (!dayTabsEl) return;

      // Cache the original parent + next sibling so we can restore on unmount or
      // when scrolling back up.
      this.dayTabsHomeParent = dayTabsEl.parentNode;
      this.dayTabsHomeNextSibling = dayTabsEl.nextSibling;

      // Create a placeholder that will sit in the original location while
      // .day-tabs is teleported, preserving layout flow.
      this.placeholderEl = document.createElement('div');
      this.placeholderEl.className = 'day-tabs__placeholder';
      this.placeholderEl.setAttribute('aria-hidden', 'true');

      // A single scroll/resize handler decides both teleport state and synced
      // left/width, comparing the natural position against the live --sticky-offset.
      // Using IntersectionObserver isn't viable here because the threshold is
      // dynamic (depends on --sticky-offset which changes as the nav hides/shows).
      this.updateTeleportState = this.updateTeleportState.bind(this);
      this.scheduleResizeStabilize = this.scheduleResizeStabilize.bind(this);
      window.addEventListener('resize', this.updateTeleportState);
      // The site nav recomputes --sticky-offset during resize and passes through
      // transient values before settling. updateTeleportState reads --sticky-offset
      // every resize event, so the live values can latch the wrong teleport state.
      // This trailing-edge debounce re-runs once after the resize stream stops,
      // when --sticky-offset has stabilized.
      window.addEventListener('resize', this.scheduleResizeStabilize);
      window.addEventListener('scroll', this.updateTeleportState, { passive: true });

      // Initial check so first paint is correct (e.g. if the user navigates in
      // already scrolled past the natural position).
      this.setupSectionObserver();
      this.updateTeleportState();
    },

    teardownTeleport() {
      this.teardownSectionObserver();
      window.removeEventListener('resize', this.updateTeleportState);
      window.removeEventListener('resize', this.scheduleResizeStabilize);
      window.removeEventListener('scroll', this.updateTeleportState);
      if (this.resizeStabilizeTimer) {
        clearTimeout(this.resizeStabilizeTimer);
        this.resizeStabilizeTimer = null;
      }

      // If currently teleported, restore .day-tabs to its home before unmounting
      // so Vue's vdom can detach it cleanly.
      if (this.isTeleported) {
        this.restoreFromBody();
      }
      this.placeholderEl = null;
      this.dayTabsHomeParent = null;
      this.dayTabsHomeNextSibling = null;
    },

    setupSectionObserver() {
      const section = this.$el.closest('.section-tabs__section');
      if (!section) return;
      this.observedSection = section;
      this.sectionObserver = new MutationObserver(() => {
        const el = this.observedSection;
        if (!el) return;
        if (!el.classList.contains('active')) {
          this.setTeleported(false);
        } else {
          this.updateTeleportState();
        }
      });
      this.sectionObserver.observe(this.observedSection, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    teardownSectionObserver() {
      if (!this.sectionObserver) return;
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
      this.observedSection = null;
    },

    scheduleResizeStabilize() {
      if (this.resizeStabilizeTimer) clearTimeout(this.resizeStabilizeTimer);
      this.resizeStabilizeTimer = setTimeout(() => {
        this.resizeStabilizeTimer = null;
        this.updateTeleportState();
      }, 300);
    },

    setTeleported(shouldTeleport) {
      if (shouldTeleport === this.isTeleported) return;
      if (shouldTeleport) {
        this.teleportToBody();
      } else {
        this.restoreFromBody();
      }
      this.isTeleported = shouldTeleport;
    },

    teleportToBody() {
      const dayTabsEl = this.$refs.dayTabs;
      if (!dayTabsEl || !this.placeholderEl || !this.dayTabsHomeParent) return;

      // Capture the current natural height before removing from flow so the
      // placeholder takes the right amount of vertical space.
      const naturalHeight = dayTabsEl.offsetHeight;
      this.placeholderEl.style.height = naturalHeight + 'px';

      // Insert placeholder where .day-tabs was, then move .day-tabs to body.
      this.dayTabsHomeParent.insertBefore(this.placeholderEl, dayTabsEl);
      document.body.appendChild(dayTabsEl);
      // Imperative add for immediate effect — isTeleported flips on the next
      // line and Vue re-renders the class binding on the next microtask, but
      // the class needs to be present before any paint between the move and
      // that microtask. The :class binding (`day-tabs--teleported: isTeleported`)
      // takes over from here so subsequent re-renders triggered by other
      // bindings (e.g. --stacked) don't wipe it via setAttribute('class', ...).
      dayTabsEl.classList.add('day-tabs--teleported');

      // Sync left/width inline (do NOT call updateTeleportState here — it
      // re-evaluates teleport state and recurses while this.isTeleported is
      // still false, breaking the state machine).
      const placeholderRect = this.placeholderEl.getBoundingClientRect();
      dayTabsEl.style.left = placeholderRect.left + 'px';
      dayTabsEl.style.width = placeholderRect.width + 'px';
    },

    restoreFromBody() {
      const dayTabsEl = this.$refs.dayTabs;
      if (!dayTabsEl || !this.placeholderEl || !this.dayTabsHomeParent) return;

      dayTabsEl.classList.remove('day-tabs--teleported');
      dayTabsEl.style.left = '';
      dayTabsEl.style.width = '';

      // Move .day-tabs back to its original spot, then remove placeholder.
      this.dayTabsHomeParent.insertBefore(dayTabsEl, this.placeholderEl);
      if (this.placeholderEl.parentNode) {
        this.placeholderEl.parentNode.removeChild(this.placeholderEl);
      }
    },

    updateTeleportState() {
      const dayTabsEl = this.$refs.dayTabs;
      if (!dayTabsEl) return;

      if (this.observedSection && !this.observedSection.classList.contains('active')) {
        this.setTeleported(false);
        return;
      }

      // Reference element for natural position:
      //   - when teleported: the placeholder (which sits where .day-tabs used to)
      //   - when inline:     .day-tabs itself
      const referenceEl = this.isTeleported ? this.placeholderEl : dayTabsEl;
      if (!referenceEl || !referenceEl.parentNode) return;
      const naturalRect = referenceEl.getBoundingClientRect();

      const stickyOffsetStr = getComputedStyle(document.documentElement)
        .getPropertyValue('--sticky-offset');
      const stickyOffset = parseFloat(stickyOffsetStr) || 0;

      // Teleport when the natural position has scrolled above (or to) the
      // sticky-offset line — that's where .day-tabs would sit when fixed. Restore
      // when the natural position is below the line.
      const shouldTeleport = naturalRect.top <= stickyOffset;
      this.setTeleported(shouldTeleport);

      // Keep the teleported element's left/width matched to the placeholder
      // and the placeholder's height matched to the (potentially-changed)
      // natural height of the day-tabs (e.g., after a stack/unstack switch).
      if (this.isTeleported) {
        this.placeholderEl.style.height = dayTabsEl.offsetHeight + 'px';
        const placeholderRect = this.placeholderEl.getBoundingClientRect();
        dayTabsEl.style.left = placeholderRect.left + 'px';
        dayTabsEl.style.width = placeholderRect.width + 'px';
      }
    },
  },
};
